import { Location } from "@angular/common";
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpParams,
    HttpRequest,
} from "@angular/common/http";
import { inject, Injectable, TransferState } from "@angular/core";
import { Observable } from "rxjs";

import { Context } from "@hermes/core-app-core";

import {
    A_TEST_STATE_KEY,
    ABTestTransferState,
    isABTestQueryParameter,
    isPathMatchingRoutes,
    QUERY_PARAMETER_ROUTES_FOR_AB_TEST,
} from "@hermes/utils-generic-services-ab-testing";

/**
 * The aim of this interceptor is to forward A/B Test query params (set by Cloudflare worker) to backend from server side.
 * And for client side, it will add query params from transferState.
 *
 * /!\ It will only forward/send A/B Tests query params of the current locale
 */
@Injectable()
export class ABTestInterceptor implements HttpInterceptor {
    readonly #context = inject(Context);
    readonly #location = inject(Location);
    readonly #transferState = inject(TransferState);

    public intercept<T, U>(
        request: HttpRequest<T>,
        next: HttpHandler,
    ): Observable<HttpEvent<U>> {
        if (this.#context.isInServerMode()) {
            return next.handle(this.addABTestQueryParamsFromLocation(request));
        }

        return next.handle(this.addABTestQueryParamsFromTransferState(request));
    }

    private addABTestQueryParamsFromLocation<T>(
        request: HttpRequest<T>,
    ): HttpRequest<T> {
        let params: HttpParams = request.params;

        const searchParameters: URLSearchParams = new URL(
            this.#location.path(false),
            "https://dummy",
        ).searchParams;
        const routesTargeted =
            this.getRoutesTargetedForABTest(searchParameters);

        if (this.shouldForwardQueryParameters(request.url, routesTargeted)) {
            for (const [name, value] of searchParameters.entries()) {
                if (isABTestQueryParameter(name)) {
                    params = params.set(name, value);

                    this.#transferState.set(A_TEST_STATE_KEY, {
                        name,
                        variant: value,
                        routes: routesTargeted,
                    });

                    // There can have only one A/B Test per page
                    break;
                }
            }
        }

        return params === request.params ? request : request.clone({ params });
    }

    private addABTestQueryParamsFromTransferState<T>(
        request: HttpRequest<T>,
    ): HttpRequest<T> {
        let params: HttpParams = request.params;
        const abFromTransferState = this.#transferState.get(
            A_TEST_STATE_KEY,
            undefined,
        );

        if (this.shouldAddQueryParameters(request.url, abFromTransferState)) {
            params = params.set(
                abFromTransferState?.name as string,
                abFromTransferState?.variant as string,
            );

            return request.clone({ params });
        }

        return request;
    }

    private getRoutesTargetedForABTest(
        searchParameters: URLSearchParams,
    ): string[] {
        const routesAsString =
            searchParameters.get(QUERY_PARAMETER_ROUTES_FOR_AB_TEST) ??
            undefined;

        return routesAsString ? routesAsString.split(",") : [];
    }

    private shouldForwardQueryParameters(
        url: string,
        routesTargeted: string[],
    ): boolean {
        if (routesTargeted.length === 0) {
            return false;
        }

        return isPathMatchingRoutes(this.getPathName(url), routesTargeted);
    }

    private shouldAddQueryParameters(
        url: string,
        abFromTransferState: ABTestTransferState | undefined,
    ): boolean {
        if (!abFromTransferState || abFromTransferState.routes.length === 0) {
            return false;
        }

        return isPathMatchingRoutes(
            this.getPathName(url),
            abFromTransferState.routes,
        );
    }

    private getPathName(url: string): string {
        return new URL(url, "https://dummy").pathname;
    }
}
